import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getParty, createParty, updateParty } from '../services/firestore';

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';

import { ArrowLeft, Save, Loader2, Building2 } from 'lucide-react';
import { toast } from 'sonner';

export const PartyFormPage = () => {

  const { id } = useParams();
  const navigate = useNavigate();
  const { userData } = useAuth();

  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    gst_number: '',
    state: '',
    phone: '',
    email: '',
    contact_person: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEdit) {
      fetchParty();
    }
  }, [id]);

  /* =========================
     FETCH PARTY
  ========================= */

  const fetchParty = async () => {

    try {

      const party = await getParty(id);

      if (!party) {
        toast.error("Party not found");
        navigate("/parties");
        return;
      }

      setFormData({

        name: party.party_name || '',
        address: party.address || '',
        gst_number: party.GST_number || '',
        state: party.state || '',
        phone: party.phone || party.mobile || '',
        email: party.email || '',
        contact_person: party.contact_person || '',
        notes: party.notes || ''

      });

    } catch (error) {

      console.error(error);
      toast.error("Failed to load party");
      navigate("/parties");

    } finally {

      setLoading(false);

    }

  };

  /* =========================
     VALIDATION
  ========================= */

  const validateForm = () => {

    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Party name is required";
    }

    if (
      formData.gst_number &&
      !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(formData.gst_number)
    ) {
      newErrors.gst_number = "Invalid GST format";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;

  };

  /* =========================
     INPUT CHANGE
  ========================= */

  const handleChange = (e) => {

    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    /* Auto detect state from GST */

    if (name === "gst_number" && value.length >= 2) {

      const stateCode = value.substring(0,2);

      const stateMap = {
        '27':'Maharashtra',
        '29':'Karnataka',
        '36':'Telangana',
        '32':'Kerala',
        '33':'Tamil Nadu',
        '24':'Gujarat'
      };

      if (stateMap[stateCode]) {

        setFormData(prev => ({
          ...prev,
          state: stateMap[stateCode]
        }));

      }

    }

  };

  /* =========================
     SUBMIT
  ========================= */

  const handleSubmit = async (e) => {

    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix errors before saving");
      return;
    }

    setSaving(true);

    try {

      /* Map UI fields → Firestore schema */

      const dataToSave = {

        party_name: formData.name,

        GST_number: formData.gst_number || "",

        address: formData.address || "",

        state: formData.state || "",

        mobile: formData.phone || "",

        email: formData.email || "",

        contact_person: formData.contact_person || "",

        notes: formData.notes || ""

      };

      if (isEdit) {

        await updateParty(id, dataToSave);

        toast.success("Party updated successfully");

      } else {

        await createParty(dataToSave, userData?.uid);

        toast.success("Party created successfully");

      }

      navigate("/parties");

    } catch (error) {

      console.error(error);

      toast.error(
        isEdit
          ? "Failed to update party"
          : "Failed to create party"
      );

    } finally {

      setSaving(false);

    }

  };

  /* =========================
     LOADING UI
  ========================= */

  if (loading) {

    return (

      <div className="p-4 md:p-6 space-y-4">

        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-96" />

      </div>

    );

  }

  /* =========================
     PAGE UI
  ========================= */

  return (

    <div className="p-4 md:p-6 max-w-2xl mx-auto">

      <div className="flex items-center gap-4 mb-6">

        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/parties")}
        >
          <ArrowLeft className="w-5 h-5"/>
        </Button>

        <div>

          <h1 className="text-2xl font-bold">
            {isEdit ? "Edit Party" : "New Party"}
          </h1>

          <p className="text-slate-500 text-sm">
            {isEdit
              ? "Update party details"
              : "Add new customer / party"}
          </p>

        </div>

      </div>

      <form onSubmit={handleSubmit}>

        <Card>

          <CardHeader>

            <CardTitle className="flex items-center gap-2 text-lg">
              <Building2 className="w-5 h-5 text-amber-500"/>
              Party Details
            </CardTitle>

          </CardHeader>

          <CardContent className="space-y-4">

            {/* NAME */}

            <div className="space-y-2">

              <Label>Party Name *</Label>

              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter party name"
              />

              {errors.name && (
                <p className="text-sm text-red-500">{errors.name}</p>
              )}

            </div>

            {/* GST */}

            <div className="space-y-2">

              <Label>GST Number</Label>

              <Input
                name="gst_number"
                value={formData.gst_number}
                onChange={handleChange}
                placeholder="27AABCU9603R1ZM"
                className="uppercase"
              />

            </div>

            {/* STATE */}

            <div className="space-y-2">

              <Label>State</Label>

              <Input
                name="state"
                value={formData.state}
                onChange={handleChange}
              />

            </div>

            {/* ADDRESS */}

            <div className="space-y-2">

              <Label>Address</Label>

              <Textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                rows={3}
              />

            </div>

            {/* CONTACT */}

            <div className="space-y-2">

              <Label>Contact Person</Label>

              <Input
                name="contact_person"
                value={formData.contact_person}
                onChange={handleChange}
              />

            </div>

            {/* PHONE + EMAIL */}

            <div className="grid grid-cols-2 gap-4">

              <div className="space-y-2">

                <Label>Phone</Label>

                <Input
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />

              </div>

              <div className="space-y-2">

                <Label>Email</Label>

                <Input
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                />

              </div>

            </div>

          </CardContent>

        </Card>

        <div className="flex gap-3 mt-6">

          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={() => navigate("/parties")}
          >
            Cancel
          </Button>

          <Button
            type="submit"
            className="flex-1 solar-gradient"
            disabled={saving}
          >

            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin"/>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2"/>
                {isEdit ? "Update" : "Create"} Party
              </>
            )}

          </Button>

        </div>

      </form>

    </div>

  );

};

export default PartyFormPage;